import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts'; // HORIZON UI'IN KRALI APEXCHARTS
import {
    MdNotificationsActive,
    MdShowChart,
    MdPhotoLibrary,
    MdPeople,
    MdDashboard,
    MdCalendarToday,
    MdTrendingUp,
    MdTrendingDown,
    MdWork,
    MdPieChart,
    MdAccountBalance,
    MdAttachMoney,
    MdReceipt // Adisyon ikonu için eklendi
} from 'react-icons/md';

import { transactionsAPI, appointmentsAPI, usersAPI, bankAccountsAPI } from '../../../services/api';

// --- Yardımcı Fonksiyonlar ---
const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value || 0);
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
};

// --- SKELETON LOADER KOMPONENTİ ---
const DashboardSkeleton = () => (
    <div className="flex w-full flex-col gap-6 animate-pulse">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            {[...Array(5)].map((_, i) => (
                <div key={i} className="h-28 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
            ))}
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="h-36 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
            ))}
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="h-[350px] w-full rounded-[20px] bg-gray-200 dark:bg-navy-700 lg:col-span-2" />
            <div className="h-[350px] w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
        </div>
        <div className="h-64 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
    </div>
);

const AdminDashboard = () => {
    const navigate = useNavigate();

    // --- State ---
    const [loading, setLoading] = useState(true);

    // Özet İstatistikler
    const [stats, setStats] = useState({
        daily: { income: 0, expense: 0, count: 0 },
        weekly: { income: 0, expense: 0, count: 0 },
        monthly: { income: 0, expense: 0, count: 0 },
        yearly: { income: 0, expense: 0, count: 0 }
    });

    // Grafikler ve Listeler
    const [chartData, setChartData] = useState({ labels: [], income: [], expense: [] });
    const [recentTransactions, setRecentTransactions] = useState([]);
    const [bankStats, setBankStats] = useState({ labels: [], data: [] });

    // --- Veri Çekme (WORK LOGIC BİREBİR AYNI) ---
    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const today = new Date();
            const todayStr = today.toISOString().split('T')[0];
            const currentYear = today.getFullYear();
            const startOfYear = `${currentYear}-01-01`;

            const dayOfWeek = today.getDay() || 7;
            const startOfWeekDate = new Date(today);
            startOfWeekDate.setDate(today.getDate() - dayOfWeek + 1);
            const startOfWeekStr = startOfWeekDate.toISOString().split('T')[0];

            const startOfMonthStr = `${currentYear}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;

            const [appRes, transRes, bankRes] = await Promise.all([
                appointmentsAPI.getAll(),
                transactionsAPI.getAll({ start_date: startOfYear }),
                bankAccountsAPI.getAll()
            ]);

            const allAppointments = appRes.appointments || [];
            const allTransactions = transRes.transactions || [];
            const allBanks = bankRes.bank_accounts || [];

            // --- 1. İSTATİSTİK HESAPLAMA ---
            const newStats = {
                daily: { income: 0, expense: 0, count: 0 },
                weekly: { income: 0, expense: 0, count: 0 },
                monthly: { income: 0, expense: 0, count: 0 },
                yearly: { income: 0, expense: 0, count: 0 }
            };

            allTransactions.forEach(tx => {
                const amt = parseFloat(tx.amount);
                const txDate = tx.date.split('T')[0];
                const isIncome = tx.type === 'income';

                if (isIncome) newStats.yearly.income += amt; else newStats.yearly.expense += amt;
                if (txDate >= startOfMonthStr) {
                    if (isIncome) newStats.monthly.income += amt; else newStats.monthly.expense += amt;
                }
                if (txDate >= startOfWeekStr) {
                    if (isIncome) newStats.weekly.income += amt; else newStats.weekly.expense += amt;
                }
                if (txDate === todayStr) {
                    if (isIncome) newStats.daily.income += amt; else newStats.daily.expense += amt;
                }
            });

            allAppointments.forEach(app => {
                if (app.status === 'cancelled') return;
                const appDate = app.appointment_date.split('T')[0];

                if (appDate.startsWith(currentYear.toString())) newStats.yearly.count++;
                if (appDate >= startOfMonthStr) newStats.monthly.count++;
                if (appDate >= startOfWeekStr) newStats.weekly.count++;
                if (appDate === todayStr) newStats.daily.count++;
            });

            setStats(newStats);

            // --- 2. GRAFİK VERİSİ ---
            const days = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
            const incomeData = Array(7).fill(0);
            const expenseData = Array(7).fill(0);

            allTransactions.forEach(tx => {
                const txDate = new Date(tx.date);
                const txDateStr = tx.date.split('T')[0];

                if (txDateStr >= startOfWeekStr) {
                    let dayIndex = txDate.getDay() - 1;
                    if (dayIndex === -1) dayIndex = 6;
                    if (tx.type === 'income') incomeData[dayIndex] += parseFloat(tx.amount);
                    else expenseData[dayIndex] += parseFloat(tx.amount);
                }
            });
            setChartData({ labels: days, income: incomeData, expense: expenseData });

            // --- 3. KASA DAĞILIMI ---
            const accountMap = { 'Nakit': 0 };
            allBanks.forEach(b => accountMap[b.name] = 0);

            allTransactions.forEach(tx => {
                const txDateStr = tx.date.split('T')[0];
                if (txDateStr >= startOfMonthStr && tx.type === 'income') {
                    const amt = parseFloat(tx.amount);
                    if (tx.payment_method === 'cash') {
                        accountMap['Nakit'] += amt;
                    } else if (tx.bank_account_id) {
                        const bankName = allBanks.find(b => b.id === tx.bank_account_id)?.name;
                        if (bankName) accountMap[bankName] += amt;
                    }
                }
            });

            setBankStats({
                labels: Object.keys(accountMap),
                data: Object.values(accountMap)
            });

            // --- 4. SON İŞLEMLER ---
            const sortedTrans = [...allTransactions]
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .slice(0, 10);
            setRecentTransactions(sortedTrans);

        } catch (err) {
            console.error("Admin Dashboard Yükleme Hatası:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    if (loading) {
        return <div className="mt-5"><DashboardSkeleton /></div>;
    }

    // Yıllık Kar Oranı Hesaplaması
    const totalYearlyVolume = stats.yearly.income + stats.yearly.expense || 1;
    const yearlyProfitPercentage = ((stats.yearly.income / totalYearlyVolume) * 100).toFixed(0);

    // --- APEXCHARTS YAPILANDIRMALARI (Horizon UI Standartları) ---
    const lineChartOptions = {
        chart: { toolbar: { show: false }, dropShadow: { enabled: true, top: 13, left: 0, blur: 10, opacity: 0.1, color: "#4318FF" } },
        colors: ["#22c55e", "#ef4444"],
        markers: { size: 0, colors: "white", strokeColors: ["#22c55e", "#ef4444"], strokeWidth: 3, strokeOpacity: 0.9, strokeDashArray: 0, fillOpacity: 1, shape: "circle", radius: 2 },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", type: "line", width: 3 },
        xaxis: { type: "category", categories: chartData.labels, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#A3AED0", fontSize: "12px", fontWeight: "500" } } },
        yaxis: { show: false },
        legend: { show: false },
        grid: { show: false, column: { color: ["#7551FF", "#39B8FF"], opacity: 0.5 }, margin: { top: 0, right: 0, bottom: 0, left: 0 } },
        tooltip: { theme: "dark" }
    };

    const lineChartSeries = [
        { name: "Gelir", data: chartData.income },
        { name: "Gider", data: chartData.expense }
    ];

    const barChartOptions = {
        chart: { toolbar: { show: false } },
        tooltip: { style: { fontSize: "12px", fontFamily: undefined }, onDatasetHover: { style: { fontSize: "12px", fontFamily: undefined } }, theme: "dark" },
        xaxis: { categories: bankStats.labels, show: false, labels: { show: false, style: { colors: "#A3AED0", fontSize: "14px", fontWeight: "500" } }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { show: true, color: "black", labels: { show: true, style: { colors: "#CBD5E1", fontSize: "14px" } } },
        grid: { show: false, strokeDashArray: 5, yaxis: { lines: { show: true } }, xaxis: { lines: { show: false } } },
        fill: { type: "solid", colors: ["#4318FF", "#F59E0B", "#EF4444", "#10B981", "#3B82F6"] },
        dataLabels: { enabled: false },
        plotOptions: { bar: { borderRadius: 5, columnWidth: "30px", horizontal: true } }
    };

    const barChartSeries = [
        { name: "Tutar", data: bankStats.data }
    ];

    return (
      <div className="mt-5 flex w-full flex-col gap-6">
        {/* ========================================
          1. BÖLÜM: HIZLI ERİŞİM VE YÖNLENDİRMELER
          ======================================== */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          {/* Adisyon Butonu - Özel Olarak İsteğin Üzerine Eklendi */}
          <button
            onClick={() => navigate("/vault/adisyon")}
            className="group flex flex-col items-center justify-center rounded-[20px] border border-gray-100 bg-white p-5 shadow-3xl shadow-shadow-500 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:border-white/10 dark:bg-navy-800 dark:shadow-none"
          >
            <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-green-50 text-green-500 transition-transform group-hover:scale-110 dark:bg-green-500/10 dark:text-green-400">
              <MdReceipt className="text-2xl" />
            </div>
            <span className="text-xs font-bold text-navy-700 dark:text-white md:text-sm">
              Adisyon
            </span>
          </button>

          <button
            onClick={() => navigate("/admin/reminders")}
            className="group flex flex-col items-center justify-center rounded-[20px] bg-gradient-to-br from-indigo-500 to-purple-600 p-5 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-indigo-500/30"
          >
            <MdNotificationsActive className="mb-2 text-3xl text-white opacity-90 transition-transform group-hover:scale-110" />
            <span className="text-xs font-bold text-white md:text-sm">
              Hatırlatıcılar
            </span>
          </button>

          <button
            onClick={() => navigate("/admin/transaction/analytics")}
            className="group flex flex-col items-center justify-center rounded-[20px] bg-gradient-to-br from-cyan-500 to-blue-600 p-5 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-cyan-500/30"
          >
            <MdShowChart className="mb-2 text-3xl text-white opacity-90 transition-transform group-hover:scale-110" />
            <span className="text-xs font-bold text-white md:text-sm">
              Finans Analizi
            </span>
          </button>

          <button
            onClick={() => navigate("/admin/gallery")}
            className="group flex flex-col items-center justify-center rounded-[20px] bg-gradient-to-br from-amber-400 to-orange-500 p-5 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-orange-500/30"
          >
            <MdPhotoLibrary className="mb-2 text-3xl text-white opacity-90 transition-transform group-hover:scale-110" />
            <span className="text-xs font-bold text-white md:text-sm">
              Galeri Yönetimi
            </span>
          </button>

          <button
            onClick={() => navigate("/admin/staff")}
            className="group flex flex-col items-center justify-center rounded-[20px] bg-gradient-to-br from-gray-700 to-gray-900 p-5 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-gray-700/30"
          >
            <MdPeople className="mb-2 text-3xl text-white opacity-90 transition-transform group-hover:scale-110" />
            <span className="text-xs font-bold text-white md:text-sm">
              Personel
            </span>
          </button>
        </div>

        {/* ========================================
          2. BÖLÜM: KAPSAMLI İSTATİSTİK KARTLARI
          ======================================== */}
        <div>
          <h4 className="mb-4 flex items-center text-lg font-bold text-navy-700 dark:text-white">
            <MdDashboard className="mr-2 text-brand-500" /> Genel İşletme Raporu
          </h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* GÜNLÜK */}
            <div className="flex flex-col rounded-[20px] bg-white bg-clip-border p-5 shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none">
              <span className="mb-3 text-xs font-bold uppercase tracking-widest text-gray-400">
                Bugün
              </span>
              <div className="mb-3 flex items-center justify-between border-b border-gray-100 pb-3 dark:border-white/10">
                <span className="flex items-center text-sm font-medium text-gray-600 dark:text-gray-300">
                  <MdCalendarToday className="mr-2" /> Randevu
                </span>
                <span className="text-xl font-bold text-navy-700 dark:text-white">
                  {stats.daily.count}
                </span>
              </div>
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center text-xs font-medium text-green-500">
                  <MdTrendingUp className="mr-1" /> Gelir
                </span>
                <span className="text-sm font-bold text-green-500">
                  {formatCurrency(stats.daily.income)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center text-xs font-medium text-red-500">
                  <MdTrendingDown className="mr-1" /> Gider
                </span>
                <span className="text-sm font-bold text-red-500">
                  {formatCurrency(stats.daily.expense)}
                </span>
              </div>
            </div>

            {/* HAFTALIK */}
            <div className="flex flex-col rounded-[20px] bg-white bg-clip-border p-5 shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none">
              <span className="mb-3 text-xs font-bold uppercase tracking-widest text-brand-500">
                Bu Hafta
              </span>
              <div className="mb-3 flex items-center justify-between border-b border-gray-100 pb-3 dark:border-white/10">
                <span className="flex items-center text-sm font-medium text-gray-600 dark:text-gray-300">
                  <MdCalendarToday className="mr-2" /> Randevu
                </span>
                <span className="text-xl font-bold text-navy-700 dark:text-white">
                  {stats.weekly.count}
                </span>
              </div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Gelir Toplamı
                </span>
                <span className="text-sm font-bold text-green-500">
                  {formatCurrency(stats.weekly.income)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Gider Toplamı
                </span>
                <span className="text-sm font-bold text-red-500">
                  {formatCurrency(stats.weekly.expense)}
                </span>
              </div>
            </div>

            {/* AYLIK */}
            <div className="flex flex-col rounded-[20px] bg-white bg-clip-border p-5 shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none">
              <span className="mb-3 text-xs font-bold uppercase tracking-widest text-amber-500">
                Bu Ay
              </span>
              <div className="mb-3 flex items-center justify-between border-b border-gray-100 pb-3 dark:border-white/10">
                <span className="flex items-center text-sm font-medium text-gray-600 dark:text-gray-300">
                  <MdCalendarToday className="mr-2" /> Randevu
                </span>
                <span className="text-xl font-bold text-navy-700 dark:text-white">
                  {stats.monthly.count}
                </span>
              </div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Gelir Toplamı
                </span>
                <span className="text-sm font-bold text-green-500">
                  {formatCurrency(stats.monthly.income)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Gider Toplamı
                </span>
                <span className="text-sm font-bold text-red-500">
                  {formatCurrency(stats.monthly.expense)}
                </span>
              </div>
            </div>

            {/* YILLIK */}
            <div className="border-transparent flex flex-col rounded-[20px] border bg-white bg-clip-border p-5 shadow-3xl shadow-shadow-500 dark:border-brand-500/20 dark:!bg-navy-800 dark:shadow-none">
              <span className="mb-3 text-xs font-bold uppercase tracking-widest text-blue-500">
                Yıllık Genel
              </span>
              <div className="mb-3 flex items-center justify-between border-b border-gray-100 pb-3 dark:border-white/10">
                <span className="flex items-center text-sm font-medium text-gray-600 dark:text-gray-300">
                  <MdWork className="mr-2" /> Toplam İşlem
                </span>
                <span className="text-xl font-bold text-navy-700 dark:text-white">
                  {stats.yearly.count}
                </span>
              </div>
              <div className="mb-2">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Yıllık Ciro
                </span>
                <span className="block text-2xl font-bold text-navy-700 dark:text-white">
                  {formatCurrency(stats.yearly.income)}
                </span>
              </div>
              <div className="mt-2 h-1.5 w-full rounded-full bg-gray-100 dark:bg-navy-700">
                <div
                  className="h-1.5 rounded-full bg-brand-500 transition-all"
                  style={{ width: `${yearlyProfitPercentage}%` }}
                ></div>
              </div>
              <span className="mt-1 block text-right text-[10px] font-medium text-gray-400">
                Ciro/Hacim Oranı
              </span>
            </div>
          </div>
        </div>

        {/* ========================================
          3. BÖLÜM: APEXCHARTS İLE ANALİZ GRAFİKLERİ
          ======================================== */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Çizgi Grafik */}
          <div className="flex flex-col rounded-[20px] bg-white bg-clip-border p-6 shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none lg:col-span-2">
            <div className="mb-2 flex items-center justify-between">
              <h5 className="flex items-center text-lg font-bold text-navy-700 dark:text-white">
                <MdPieChart className="mr-2 text-brand-500" /> Haftalık Genel
                Gelir & Giderler
              </h5>
            </div>
            <div className="h-[300px] w-full">
              <Chart
                options={lineChartOptions}
                series={lineChartSeries}
                type="area"
                width="100%"
                height="100%"
              />
            </div>
          </div>

          {/* Bar Grafik */}
          <div className="flex flex-col rounded-[20px] bg-white bg-clip-border p-6 shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none">
            <div className="mb-2 flex items-center justify-between">
              <h5 className="flex items-center text-lg font-bold text-navy-700 dark:text-white">
                <MdAccountBalance className="mr-2 text-amber-500" /> Kasa
                Dağılımı (Bu Ay)
              </h5>
            </div>
            <div className="h-[300px] w-full">
              <Chart
                options={barChartOptions}
                series={barChartSeries}
                type="bar"
                width="100%"
                height="100%"
              />
            </div>
          </div>
        </div>

        {/* ========================================
          4. BÖLÜM: SON FİNANSAL HAREKETLER (Responsive Card/Table)
          ======================================== */}
        <div className="flex flex-col rounded-[20px] bg-white bg-clip-border p-6 shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none">
          <div className="mb-6 flex items-center justify-between">
            <h5 className="flex items-center text-lg font-bold text-navy-700 dark:text-white">
              <MdAttachMoney className="mr-2 text-green-500" /> Son Finansal
              Hareketler
            </h5>
            <button
              onClick={() => navigate("/admin/transaction/list")}
              className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-brand-400"
            >
              Tümünü Gör
            </button>
          </div>

          {/* MOBİL GÖRÜNÜM: KART YAPISI */}
          <div className="flex flex-col gap-4 md:hidden">
            {recentTransactions.length === 0 ? (
              <div className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                İşlem yok
              </div>
            ) : (
              recentTransactions.map((tx) => (
                <div
                  key={tx.id}
                  className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 p-4 dark:border-white/10 dark:bg-navy-900"
                >
                  <div>
                    <h6 className="text-sm font-bold text-navy-700 dark:text-white">
                      {tx.title || (tx.type === "income" ? "Gelir" : "Gider")}
                    </h6>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(tx.date)} •{" "}
                      {tx.staff ? tx.staff.name : "Genel"}
                    </p>
                  </div>
                  <div className="text-right">
                    <span
                      className={`block text-sm font-bold ${
                        tx.type === "income" ? "text-green-500" : "text-red-500"
                      }`}
                    >
                      {tx.type === "income" ? "+" : "-"}
                      {formatCurrency(tx.amount)}
                    </span>
                    <span className="text-[10px] font-medium text-gray-400">
                      {tx.payment_method === "cash" ? "Nakit" : "Kart/Banka"}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* DESKTOP GÖRÜNÜM: TEMİZ TABLO */}
          <div className="hidden w-full overflow-x-auto md:block">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200 text-xs font-bold tracking-wide text-gray-400 dark:border-white/10">
                  <th className="pb-3 pl-2">İŞLEM DETAYI</th>
                  <th className="pb-3 pr-2 text-right">TUTAR</th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.length === 0 ? (
                  <tr>
                    <td
                      colSpan="2"
                      className="py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      İşlem yok
                    </td>
                  </tr>
                ) : (
                  recentTransactions.map((tx) => (
                    <tr
                      key={tx.id}
                      className="border-b border-gray-50 transition-colors hover:bg-gray-50 dark:border-white/5 dark:hover:bg-navy-700/50"
                    >
                      <td className="py-3 pl-2">
                        <div className="text-sm font-bold text-navy-700 dark:text-white">
                          {tx.title ||
                            (tx.type === "income" ? "Gelir" : "Gider")}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(tx.date)} <span className="mx-1">•</span>{" "}
                          {tx.staff ? tx.staff.name : "Genel"}
                        </div>
                      </td>
                      <td className="py-3 pr-2 text-right">
                        <div
                          className={`text-sm font-bold ${
                            tx.type === "income"
                              ? "text-green-500"
                              : "text-red-500"
                          }`}
                        >
                          {tx.type === "income" ? "+" : "-"}
                          {formatCurrency(tx.amount)}
                        </div>
                        <div className="text-xs text-gray-400">
                          {tx.payment_method === "cash"
                            ? "Nakit"
                            : "Kart/Banka"}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
};

export default AdminDashboard;